import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
      {title}
    </h2>
    <div className="text-gray-700 leading-7 space-y-3">
      {children}
    </div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4">
        <article className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Términos y Condiciones
          </h1>

          <p className="mt-3 text-gray-500">
            Última actualización: 4 de agosto de 2026
          </p>

          <p className="mt-6 text-gray-700 leading-7">
            Estos Términos y Condiciones regulan el acceso y uso de
            petadvisor.es y de los servicios ofrecidos por PetAdvisor España.
            Al utilizar la plataforma, aceptas estas condiciones.
          </p>

          <Section title="1. Identificación y contacto">
            <p>
              PetAdvisor España opera a través del sitio web petadvisor.es.
            </p>
            <p>
              Correo de contacto:{' '}
              <a
                href="mailto:hola@petadvisor.es"
                className="font-semibold underline"
              >
                hola@petadvisor.es
              </a>
              .
            </p>
          </Section>

          <Section title="2. Finalidad de la plataforma">
            <p>
              PetAdvisor permite buscar, consultar, comparar y contactar con
              veterinarias, tiendas, residencias, protectoras, adiestradores,
              parques y otros proveedores o servicios relacionados con las
              mascotas.
            </p>
            <p>
              Salvo que se indique expresamente lo contrario, PetAdvisor no
              presta directamente los servicios publicados ni actúa como
              veterinaria, residencia, tienda, protectora, agencia o
              representante de los proveedores.
            </p>
          </Section>

          <Section title="3. Requisitos de uso">
            <p>
              La persona usuaria debe utilizar la plataforma de forma lícita,
              responsable y respetuosa. Cuando sea menor de edad, deberá contar
              con la autorización y supervisión de sus padres, madres o
              representantes legales cuando resulte necesaria.
            </p>
          </Section>

          <Section title="4. Registro y cuentas">
            <ul className="list-disc pl-6 space-y-2">
              <li>Los datos proporcionados deben ser verdaderos y actuales.</li>
              <li>
                Cada usuario es responsable de proteger sus credenciales.
              </li>
              <li>
                No se permite crear cuentas utilizando identidades falsas ni
                suplantar a otras personas o negocios.
              </li>
              <li>
                Debes comunicar cualquier acceso no autorizado a tu cuenta.
              </li>
              <li>
                PetAdvisor puede solicitar verificaciones adicionales para
                proteger la plataforma y sus usuarios.
              </li>
            </ul>
          </Section>

          <Section title="5. Perfiles de proveedores">
            <p>
              Los proveedores son responsables de que la información publicada
              sobre sus servicios, precios, instalaciones, horarios,
              disponibilidad, autorizaciones y condiciones sea correcta.
            </p>
            <p>
              PetAdvisor puede crear fichas a partir de información pública,
              recibir datos directamente de proveedores o permitir que un
              negocio solicite la gestión de su perfil.
            </p>
            <p>
              La publicación de un perfil no representa una certificación,
              garantía o recomendación profesional por parte de PetAdvisor.
            </p>
          </Section>

          <Section title="6. Contacto y contratación con proveedores">
            <p>
              La contratación se realiza directamente entre el usuario y el
              proveedor. Antes de contratar, el usuario debe comprobar los
              precios, disponibilidad, condiciones, políticas de cancelación,
              instalaciones y cualquier requisito especial.
            </p>
            <p>
              PetAdvisor no será parte del contrato celebrado entre usuarios y
              proveedores, salvo que se informe expresamente lo contrario.
            </p>
          </Section>

          <Section title="7. Reseñas y contenido generado por usuarios">
            <p>
              Las reseñas deben basarse en experiencias reales y expresarse de
              forma respetuosa. No está permitido publicar:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Contenido falso, engañoso o manipulado.</li>
              <li>Amenazas, acoso, insultos o contenido discriminatorio.</li>
              <li>Datos personales de terceros sin autorización.</li>
              <li>Contenido que vulnere derechos de propiedad intelectual.</li>
              <li>Publicidad no autorizada, spam o enlaces maliciosos.</li>
              <li>Contenido ilegal o contrario al bienestar animal.</li>
            </ul>
            <p>
              PetAdvisor podrá revisar, limitar, ocultar o retirar contenido
              cuando existan indicios de incumplimiento, sin que ello implique
              una obligación general de supervisión previa.
            </p>
          </Section>

          <Section title="8. Normas de conducta">
            <p>No se permite:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Interferir en el funcionamiento de la plataforma.</li>
              <li>Intentar acceder a cuentas o zonas no autorizadas.</li>
              <li>Introducir malware, automatizaciones abusivas o ataques.</li>
              <li>Extraer datos de forma masiva sin autorización.</li>
              <li>Utilizar la plataforma para actividades fraudulentas.</li>
              <li>Vulnerar derechos de usuarios, proveedores o terceros.</li>
            </ul>
          </Section>

          <Section title="9. Planes, precios y pagos">
            <p>
              PetAdvisor puede ofrecer planes gratuitos y de pago. Antes de
              contratar se informará del precio, impuestos aplicables,
              duración, prestaciones y condiciones de renovación o
              cancelación.
            </p>
            <p>
              Cuando intervenga un proveedor de pagos, la operación también
              podrá estar sujeta a sus condiciones. PetAdvisor no solicita que
              se envíen contraseñas ni datos completos de tarjetas por correo
              electrónico.
            </p>
          </Section>

          <Section title="10. Renovación y cancelación">
            <p>
              Cuando un plan tenga renovación automática, esta circunstancia
              se informará antes de contratar. El usuario podrá gestionar la
              cancelación mediante los mecanismos disponibles o contactando
              con soporte.
            </p>
            <p>
              La cancelación no afectará a importes ya devengados ni a servicios
              ya utilizados, sin perjuicio de los derechos que reconozca la
              normativa de consumo aplicable.
            </p>
          </Section>

          <Section title="11. Disponibilidad y modificaciones">
            <p>
              PetAdvisor puede actualizar, modificar, suspender o interrumpir
              funciones por mantenimiento, seguridad, cambios técnicos o
              necesidades operativas. Se intentará reducir el impacto sobre
              los usuarios cuando sea razonablemente posible.
            </p>
          </Section>

          <Section title="12. Propiedad intelectual">
            <p>
              El diseño, marca, software, textos propios, estructura y demás
              elementos de PetAdvisor están protegidos por la normativa de
              propiedad intelectual e industrial.
            </p>
            <p>
              Los usuarios conservan los derechos sobre el contenido que
              publican, pero conceden a PetAdvisor una autorización no
              exclusiva para alojarlo, reproducirlo y mostrarlo en la medida
              necesaria para operar y promocionar la plataforma.
            </p>
          </Section>

          <Section title="13. Responsabilidad">
            <p>
              PetAdvisor procura mantener información útil y actualizada, pero
              no garantiza que todos los datos proporcionados por terceros
              sean completos, exactos o estén permanentemente disponibles.
            </p>
            <p>
              Cada proveedor es responsable de sus servicios, actuaciones,
              precios, instalaciones, personal y obligaciones legales. Cada
              usuario es responsable de evaluar al proveedor antes de
              contratar.
            </p>
            <p>
              Ninguna disposición de estas condiciones limita derechos
              irrenunciables ni responsabilidades que no puedan excluirse
              legalmente.
            </p>
          </Section>

          <Section title="14. Suspensión y cierre de cuentas">
            <p>
              PetAdvisor podrá suspender, limitar o cerrar cuentas cuando
              existan incumplimientos, fraude, suplantación, riesgos de
              seguridad, actividad ilegal o perjuicios para usuarios,
              proveedores o la plataforma.
            </p>
          </Section>

          <Section title="15. Protección de datos y cookies">
            <p>
              El tratamiento de datos personales se regula en la Política de
              Privacidad. El uso de cookies se informará mediante los avisos y
              mecanismos de consentimiento correspondientes.
            </p>
          </Section>

          <Section title="16. Comunicaciones">
            <p>
              Las comunicaciones relacionadas con la cuenta, seguridad,
              solicitudes, contratación o cambios relevantes podrán enviarse
              al correo electrónico asociado a la cuenta.
            </p>
          </Section>

          <Section title="17. Modificación de las condiciones">
            <p>
              Estas condiciones podrán actualizarse por cambios legales,
              técnicos o funcionales. La versión vigente se publicará en esta
              página con su fecha de actualización.
            </p>
          </Section>

          <Section title="18. Legislación y jurisdicción">
            <p>
              Estas condiciones se rigen por la legislación española y la
              normativa de la Unión Europea aplicable.
            </p>
            <p>
              Cuando el usuario tenga la condición de consumidor, cualquier
              controversia se resolverá ante los juzgados y tribunales que
              correspondan conforme a la normativa de protección de
              consumidores. En los demás casos se aplicarán las reglas legales
              de competencia territorial.
            </p>
          </Section>

          <Section title="19. Contacto">
            <p>
              Para consultas sobre estas condiciones, escribe a{' '}
              <a
                href="mailto:hola@petadvisor.es"
                className="font-semibold underline"
              >
                hola@petadvisor.es
              </a>
              .
            </p>
          </Section>
        </article>
      </div>
    </div>
  );
}
